import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { SvgIcon } from '@interlayer/js/app/components/common/SvgIcon';
import { Button } from '@interlayer/js/components/common/Button';
import { I18n } from '@interlayer/js/components/common/I18n';
import { Link } from '@interlayer/js/components/common/Link/Link';
import { Loader } from '@interlayer/js/components/common/Loader/Loader';

import { showGeoComplyPopupAction } from '@core/js/components/tagTypes/GeoComply/actions';
import { useTranslations } from '@core/js/hooks/i18n';

import { NEAREST_CLUB_COUNT } from '../../constants';

import { TNearestClubProps } from './types';

import style from './NearestClub.scss';

export const NearestClub: React.FC<TNearestClubProps> = ({
    nearestClubData,
    isUserLocationBlocked,
    isLoadingNearestSearch,
    isLoadingNearestClub
}) => {
    const dispatch = useDispatch();
    const t = useTranslations('playtech.mobile.system.club.');

    const showGeoErrorPopup = useCallback(() => {
        dispatch(showGeoComplyPopupAction({
            title: 'playtech.mobile.system.geocomply.popups.geolocationNotAvailable.title',
            content: 'playtech.mobile.system.geocomply.popups.geolocationNotAvailable.content',
            isModal: false,
            buttons: false,
            useTransparentPopup: false
        }));
    }, [dispatch]);

    const markerNearest = useMemo(() => {
        return Array.isArray(nearestClubData) ? nearestClubData.slice(0, NEAREST_CLUB_COUNT) : [];
    }, [nearestClubData]);

    if (isUserLocationBlocked || !Array.isArray(nearestClubData)) {
        return (
            <>
                <I18n
                    t={t}
                    tKey="location.title"
                    className={style.locationTitle}
                />

                <Button
                    data-testid="NearestClub_locationBtn"
                    size="medium"
                    view="link"
                    className={style.locationBtn}
                    onClick={showGeoErrorPopup}
                >
                    <SvgIcon
                        type="my-location"
                        className={style.locationIcon}
                    />

                    {t('location.button')}
                </Button>
            </>
        );
    }

    if (isLoadingNearestSearch || isLoadingNearestClub) {
        return <Loader className={style.loader} />;
    }

    return (
        <div data-testid="NearestClub_container" className={style.nearestClubColumn}>
            {markerNearest.map(({ Name, Distance }) => (
                <Link
                    data-testid={`NearestClub_link-${Name}`}
                    href={`/club/${Name?.toLowerCase().replace(/\s+/g, '-')}.html`}
                    className={style.nearestClubRow}
                    key={Name}
                >
                    <div className={style.nearestClubTitle}>{Name}</div>

                    <div className={style.nearestClubDistance}>{`${Distance} ${t('milesaway')}`}</div>

                    <div className={style.nearestClubLink}>
                        <SvgIcon className={style.nearestClubLinkIcon} type="arrow-right" />
                    </div>
                </Link>
            ))}
        </div>
    );
};
